import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { CustomerLayout } from '@/components/layout/CustomerLayout';
import { SwipeableTabs } from '@/components/layout/SwipeableTabs';
import { PendingDataHandler } from '@/components/auth/PendingDataHandler';
import { DashboardPage } from '@/pages/account/DashboardPage';
import { WalletPage } from '@/pages/account/WalletPage';
import { BatchHistoryPage } from '@/pages/account/BatchHistoryPage';
import { SearchHistoryPage } from '@/pages/account/SearchHistoryPage';
import { SettingsPage } from '@/pages/account/SettingsPage';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertCircle,
  LayoutDashboard,
  Wallet,
  FileText,
  Search,
  Settings,
} from 'lucide-react';

/**
 * Protected route wrapper that requires authentication
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useCustomerAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-collectability-blue border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="p-8 max-w-md text-center">
          <AlertCircle className="w-12 h-10 text-orange-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-slate-900 mb-2">Authentication Required</h2>
          <p className="text-slate-600 mb-6">
            Please sign in to access your customer portal
          </p>
          <div className="flex gap-3 justify-center">
            <Button variant="outline" onClick={() => window.location.href = '/'}>
              Go Home
            </Button>
            <Button onClick={() => window.location.href = `/auth${window.location.search}`}>
              Sign In
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
};

const tabs = [
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'wallet', label: 'Wallet', icon: Wallet },
  { id: 'batches', label: 'Batch History', icon: FileText },
  { id: 'searches', label: 'Search History', icon: Search },
  { id: 'settings', label: 'Settings', icon: Settings },
];

const routeToTabId = (pathname: string): string => {
  if (pathname === '/account' || pathname === '/account/') return 'dashboard';
  if (pathname.startsWith('/account/wallet')) return 'wallet';
  if (pathname.startsWith('/account/batches')) return 'batches';
  if (pathname.startsWith('/account/searches')) return 'searches';
  if (pathname.startsWith('/account/settings')) return 'settings';
  // Redirect old payment-methods route to wallet
  if (pathname.startsWith('/account/payment-methods')) return 'wallet';
  return 'dashboard';
};

const tabIdToRoute = (tabId: string): string => {
  const routes: Record<string, string> = {
    dashboard: '/account',
    wallet: '/account/wallet',
    batches: '/account/batches',
    searches: '/account/searches',
    settings: '/account/settings',
  };
  return routes[tabId] || '/account';
};

export const CustomerPortal: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = React.useState<string>(() => routeToTabId(location.pathname));

  // Sync tab with URL changes
  useEffect(() => {
    const tabId = routeToTabId(location.pathname);
    setActiveTab(tabId);
  }, [location.pathname]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    navigate(tabIdToRoute(tabId), { replace: true });
  };

  // Pages array must match tabs order
  const pages = [
    <DashboardPage key="dashboard" />,
    <WalletPage key="wallet" />,
    <BatchHistoryPage key="batches" />,
    <SearchHistoryPage key="searches" />,
    <SettingsPage key="settings" />,
  ];

  return (
    <ProtectedRoute>
      <PendingDataHandler />
      <CustomerLayout>
        <SwipeableTabs
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabChange}
          children={pages}
        />
      </CustomerLayout>
    </ProtectedRoute>
  );
};

